import React, {createContext, useContext, useReducer, useCallback} from 'react'

import {GroupedRoleAssignment} from '../modules/ManageUsers.selectors'

type UserCreationRolesState = {
  roles: GroupedRoleAssignment[]
}

type UserCreationRolesAction =
  | {type: 'ADD_ROLE'; payload: GroupedRoleAssignment}
  | {type: 'UPDATE_ROLE'; payload: {updatedRole: GroupedRoleAssignment}}
  | {type: 'DELETE_ROLE'; payload: string}

type UserCreationRolesContextType = {
  state: UserCreationRolesState
  actions: {
    addRole: (role: GroupedRoleAssignment) => void
    updateRole: (updatedRole: GroupedRoleAssignment) => void
    deleteRole: (tempId: string) => void
    getAllRoles: () => GroupedRoleAssignment[]
  }
}

const initialState: UserCreationRolesState = {
  roles: []
}

const generateTempId = () => `temp_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`

const userCreationRolesReducer = (
  state: UserCreationRolesState,
  action: UserCreationRolesAction
): UserCreationRolesState => {
  const deepCleanObject = (obj: unknown): unknown => {
    if (obj === null || obj === undefined) {
      return obj
    }

    if (Array.isArray(obj)) {
      return obj.map((item) => deepCleanObject(item)).filter((item) => item !== undefined)
    }

    if (typeof obj === 'object') {
      const cleaned: Record<string, unknown> = {}
      for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
        if (value !== undefined) {
          cleaned[key] = deepCleanObject(value)
        }
      }
      return cleaned
    }

    return obj
  }

  switch (action.type) {
    case 'ADD_ROLE': {
      const cleanedRole = deepCleanObject(action.payload) as GroupedRoleAssignment

      if (!cleanedRole.tempId) {
        cleanedRole.tempId = generateTempId()
      }

      return {
        ...state,
        roles: [...state.roles, cleanedRole]
      }
    }

    case 'UPDATE_ROLE': {
      const {updatedRole} = action.payload

      const roleIndex = state.roles.findIndex((role) => role.tempId === updatedRole.tempId)

      if (roleIndex === -1) {
        console.warn(`Role with tempId ${updatedRole.tempId} not found for update`)
        return state
      }

      const updatedRoles = [...state.roles]
      const cleanedUpdatedRole = deepCleanObject(updatedRole) as GroupedRoleAssignment

      cleanedUpdatedRole.tempId = updatedRole.tempId

      updatedRoles[roleIndex] = cleanedUpdatedRole

      return {
        ...state,
        roles: updatedRoles
      }
    }

    case 'DELETE_ROLE': {
      return {
        ...state,
        roles: state.roles.filter((role) => role.tempId !== action.payload)
      }
    }

    default:
      return state
  }
}

const UserCreationRolesContext = createContext<UserCreationRolesContextType | null>(null)

type UserCreationRolesProviderProps = {
  children: React.ReactNode
}

export const UserCreationRolesProvider: React.FC<UserCreationRolesProviderProps> = ({children}) => {
  const [state, dispatch] = useReducer(userCreationRolesReducer, initialState)

  const addRole = useCallback((role: GroupedRoleAssignment) => {
    dispatch({type: 'ADD_ROLE', payload: role})
  }, [])

  const updateRole = useCallback((updatedRole: GroupedRoleAssignment) => {
    dispatch({type: 'UPDATE_ROLE', payload: {updatedRole}})
  }, [])

  const deleteRole = useCallback((tempId: string) => {
    dispatch({type: 'DELETE_ROLE', payload: tempId})
  }, [])

  const getAllRoles = useCallback((): GroupedRoleAssignment[] => {
    return state.roles
  }, [state.roles])

  const contextValue: UserCreationRolesContextType = {
    state,
    actions: {
      addRole,
      updateRole,
      deleteRole,
      getAllRoles
    }
  }

  return (
    <UserCreationRolesContext.Provider value={contextValue}>
      {children}
    </UserCreationRolesContext.Provider>
  )
}

export const useUserCreationRoles = (): UserCreationRolesContextType => {
  const context = useContext(UserCreationRolesContext)

  if (!context) {
    throw new Error('useUserCreationRoles must be used within a UserCreationRolesProvider')
  }

  return context
}
